import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SIZE_LABELS, sizeFromHours } from '@rundown/domain';
import { formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { useSizeThresholds } from '../workspace/useWorkspace';
import { TYPE_LABELS } from '../labels';
import type { Mark, PlanCardModel } from './usePlanBoard';

/** What a card's presence in a column means, in one word. */
const TAG: Readonly<Record<Mark, { label: string; className: string } | null>> = {
  backlog: null,
  committed: { label: 'Committed', className: 'text-ink' },
  agreed: { label: 'Agreed', className: 'text-ink' },
  added: { label: 'Added', className: 'text-violet-strong' },
  removed: { label: 'Removed', className: 'text-danger' },
  placed: { label: 'Placed', className: 'text-accent' },
  projected: { label: 'Projected', className: 'text-faint' },
  delivered: { label: 'Delivered', className: 'text-success' },
  carried: { label: 'Carried over', className: 'text-warn' },
};

export function Column({
  id,
  header,
  children,
  className,
  bordered = false,
  droppable = true,
}: {
  readonly id: string;
  readonly header: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly bordered?: boolean;
  /** A frozen sprint takes no drops, so it should not light up under one. */
  readonly droppable?: boolean;
}) {
  const drop = useDroppable({ id, disabled: !droppable });
  return (
    <div
      ref={drop.setNodeRef}
      className={cx(
        'flex min-h-[26rem] flex-col gap-2 rounded-xl p-3',
        bordered && 'border border-line',
        drop.isOver && droppable && 'outline-2 outline-accent',
        className,
      )}
    >
      <div className="px-1 pt-1 pb-2">{header}</div>
      {children}
    </div>
  );
}

export function PlanCard({
  card,
  draggable,
  pending,
}: {
  readonly card: PlanCardModel;
  readonly draggable: boolean;
  readonly pending: boolean;
}) {
  // A projected card is the same request as its backlog card; the ids must
  // differ for dnd-kit, and dragging either one moves the request.
  const dragId = card.mark === 'backlog' ? card.requestId : `${card.mark}:${card.requestId}`;
  const drag = useDraggable({ id: dragId, data: card, disabled: !draggable || pending });
  const tag = TAG[card.mark];
  const sizeThresholds = useSizeThresholds();
  const size =
    card.approxHours > 0
      ? `${SIZE_LABELS[sizeFromHours(card.approxHours, sizeThresholds)]} ~${formatHours(card.approxHours)}`
      : 'Needs analysis';

  return (
    <div
      ref={drag.setNodeRef}
      {...drag.attributes}
      {...drag.listeners}
      className={cx(
        'rounded-[9px] px-3 py-2.5',
        card.mark === 'backlog' ? 'border border-line bg-surface' : 'bg-base',
        card.mark === 'projected'
          ? 'border border-dashed border-line-stronger'
          : card.mark !== 'backlog' && 'border border-line-strong',
        card.mark === 'removed' && 'opacity-60',
        draggable && !pending && 'cursor-grab touch-none active:cursor-grabbing',
        (drag.isDragging || pending) && 'opacity-50',
      )}
    >
      <div className="flex items-baseline gap-2">
        {card.rank !== undefined && <span className="fs-11 font-bold text-muted numeric">{card.rank}</span>}
        {/* No stopPropagation here: the title is where people grab a card.
            The sensor's 5px activation distance is what tells a click (open
            the request) from a drag. */}
        <Link
          to={`/requests/${card.requestId}`}
          draggable={false}
          className={cx(
            'min-w-0 flex-1 fs-12.5 leading-snug font-semibold text-ink hover:text-accent',
            card.mark === 'removed' && 'line-through',
          )}
        >
          {card.title}
        </Link>
      </div>
      <div className="mt-1.5 flex flex-wrap justify-between gap-x-2 gap-y-0.5 fs-11 text-muted">
        <span>
          {TYPE_LABELS[card.type]} · {size}
          {card.landsIn ? ` · ${card.landsIn}` : ''}
        </span>
        {tag !== null && (
          <span className={cx('font-semibold whitespace-nowrap', tag.className)}>{tag.label}</span>
        )}
      </div>
    </div>
  );
}
